import {
  URUN_KODU_YOK,
  type ReferansDurumu,
  detectScanSide,
  isAcceptedCode,
  isMatchValid,
  referansDurumu,
  type ExpectedStats,
  type ScanSide,
  type TenantAccess,
} from '@konyaxl/shared';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../core/api.js';
import {
  getNativeSummary,
  hasHardwareScanner,
  keepScreenAwake,
  onHardwareScan,
  vibrate,
} from '../core/native.js';
import { SendQueue, type QueueItem } from '../core/queue.js';
import { startScannerListener } from '../core/scanner.js';
import {
  sesEslestirmeTamam,
  sesHata,
  sesIlkOkuma,
  sesUyari,
  unlockAudio,
} from '../core/sounds.js';
import { CameraScanner, cameraSupported } from './CameraScanner.js';
import { RemainingPanel } from './RemainingPanel.js';

interface Props {
  tenant: TenantAccess;
  onBack: () => void;
}

interface Satir {
  id: string;
  codeA: string;
  codeB: string;
  durum: 'bekliyor' | 'kayitli' | 'degistirildi' | 'hata';
  not?: string;
  /** Kategorinin doğrulama kuralına uyuyor mu (Brisa'da BHJ eşleşmesi). */
  gecerli: boolean;
  /** İade akışında hangi alt kategoriye yazıldığı. */
  kategori?: string;
  /** Referans listesine göre durum (LCW). */
  referans?: ReferansDurumu;
}

/** Kullanıcıya sorulan çakışma onayı. */
interface Onay {
  kod: string;
  eslesenKod: string;
  taraf: ScanSide;
  recordId?: string;
}

interface CodeStatusResponse {
  existing: {
    id: string;
    codeA: string | null;
    codeB: string | null;
  } | null;
}

export function ScanPage({ tenant, onBack }: Props) {
  const [slotA, setSlotA] = useState('');
  const [slotB, setSlotB] = useState('');
  const [satirlar, setSatirlar] = useState<Satir[]>([]);
  const [bekleyen, setBekleyen] = useState(0);
  const [onay, setOnay] = useState<Onay | null>(null);
  const [hazir, setHazir] = useState(false);
  const [manuel, setManuel] = useState('');
  const [stats, setStats] = useState<ExpectedStats | null>(null);
  const [panelAcik, setPanelAcik] = useState(false);
  const [siraUyarisi, setSiraUyarisi] = useState<string | null>(null);
  const [onaySiliniyor, setOnaySiliniyor] = useState(false);
  const [onayHata, setOnayHata] = useState<string | null>(null);
  /**
   * Kamera tercihi hatırlanır: fiziksel okuyucusu olmayan bir telefonda
   * her açılışta kamerayı elle açtırmak gereksiz. Eski projede de bu tercih
   * saklanıyordu (rabiekle.dart:195, `lazer` anahtarı).
   */
  const [reddedilen, setReddedilen] = useState<string | null>(null);
  const [tampon, setTampon] = useState('');
  const [kameraAcik, setKameraAcik] = useState(
    () => localStorage.getItem('konyaxl.kamera') === 'acik',
  );
  /**
   * İade akışı.
   *
   * Kategori HER eklemede sorulur — sekme yalnızca listeyi ayırır, kaydın
   * kategorisini belirlemez. Bir gönderinin nereye yazıldığı sekmeye
   * bakılarak varsayılsaydı, sekme değiştirmeyi unutan operatör farkında
   * olmadan yanlış kategoriye yazardı.
   */
  const kategoriler = tenant.features.returnFlow ? tenant.subCategories : [];
  const [aktifKategori, setAktifKategori] = useState(
    () =>
      localStorage.getItem(`konyaxl.kategori.${tenant.slug}`) ??
      tenant.subCategories[0] ??
      '',
  );

  const kategoriDegistir = useCallback(
    (k: string) => {
      setAktifKategori(k);
      localStorage.setItem(`konyaxl.kategori.${tenant.slug}`, k);
    },
    [tenant.slug],
  );

  /**
   * Referans eşleştirme listesi (LCW).
   *
   * Açılışta bir kez indirilip bellekte tutuluyor: doğruluk kararı okutulan
   * anda verilmeli, ağ beklenmemeli. Sunucu da aynı kontrolü yapıyor; bu
   * yalnızca anında geri bildirim için.
   */
  const referansKural = tenant.matchRule.type === 'reference';
  const referans = useRef(new Map<string, string>());
  const [referansHazir, setReferansHazir] = useState(!referansKural);
  const [referansSayisi, setReferansSayisi] = useState(0);
  /** Listenin en son ne zaman yüklendiği — eskimişse fark edilsin. */
  const [sonYukleme, setSonYukleme] = useState<string | null>(null);
  /**
   * Son yükleme tamamlandı mı?
   *
   * Parçalı gönderim ortasında kesilirse liste yarım kalıyor ama taze
   * görünüyor. Eksik barkodlar sahada "listede yok" çıkıp gereksiz elle
   * kontrole yol açtığı için ayrıca uyarılıyor.
   */
  const [yuklemeTam, setYuklemeTam] = useState(true);
  /** Son okutulan eşleştirme listede bulunamadı — manuel kontrol gerekiyor. */
  const [listedeYok, setListedeYok] = useState<{ a: string; b: string } | null>(null);
  /**
   * Yanlış eşleştirme onayı.
   *
   * Kapatılana kadar okuma alınmıyor: yanlış eşleştirmenin fark edilmeden
   * geçmesi, listenin sonunda düzeltilmesi en pahalı hata. Kayıt yine de
   * alınıyor, panel yalnızca operatörün görmesini garantiliyor.
   */
  const [yanlisEslesme, setYanlisEslesme] = useState<{
    a: string;
    b: string;
    dogruB: string;
  } | null>(null);

  /** Kayıt oluşmadan önce sorulan kategori; seçim yapılana kadar beklenir. */
  const [kategoriSecimi, setKategoriSecimi] = useState<{
    a: string;
    b: string;
    ustuneYaz: boolean;
  } | null>(null);

  const kategoriSorulur =
    tenant.features.returnFlow && tenant.subCategories.length > 0;

  const kameraDegistir = useCallback((acik: boolean) => {
    setKameraAcik(acik);
    localStorage.setItem('konyaxl.kamera', acik ? 'acik' : 'kapali');
    if (acik) unlockAudio();
  }, []);

  /**
   * Kullanımdaki kodlar. Çakışma kontrolü buradan yapılır — ağ beklemeden,
   * anında. Sunucu yine de son sözü söyler (409), bu yalnızca hızlı yol.
   */
  const kullanilan = useRef(new Map<string, string>());
  const kuyruk = useRef<SendQueue | null>(null);
  /** Onaylanmış üzerine yazma istekleri. */
  const izinli = useRef(new Set<string>());

  const satirGuncelle = useCallback((id: string, degisiklik: Partial<Satir>) => {
    setSatirlar((eski) =>
      eski.map((s) => (s.id === id ? { ...s, ...degisiklik } : s)),
    );
  }, []);

  const onayAc = useCallback(
    (kod: string, eslesenKod: string, taraf: ScanSide) => {
      setOnay({ kod, eslesenKod, taraf });
      setOnayHata(null);

      void api
        .get<CodeStatusResponse>(
          `/${tenant.slug}/code-status?code=${encodeURIComponent(kod)}`,
        )
        .then((res) => {
          const mevcut = res.existing;
          if (!mevcut) return;

          setOnay((eski) =>
            eski?.kod === kod
              ? {
                  ...eski,
                  recordId: mevcut.id,
                  eslesenKod:
                    mevcut.codeA === kod
                      ? (mevcut.codeB ?? eski.eslesenKod)
                      : (mevcut.codeA ?? eski.eslesenKod),
                }
              : eski,
          );
        })
        .catch(() => {
          // Silme butonu kaydın id'si gelirse açılır; tarama akışı etkilenmez.
        });
    },
    [tenant.slug],
  );

  /**
   * Beklenen barkod sayaçları.
   *
   * Kayıt kuyruğu arka planda çalıştığı için sayaç sunucudan tazelenir.
   * Yerelde tahmin yürütmek yerine gerçek değeri göstermek daha güvenilir:
   * operatör "kaç kaldı" sorusuna doğru cevap görmeli.
   */
  const statsYenile = useCallback(async () => {
    // Beklenen liste kapalıysa sayaç sorgusu boşuna gider.
    if (!tenant.features.expectedList) return;
    try {
      setStats(await api.get<ExpectedStats>(`/${tenant.slug}/expected/stats`));
    } catch {
      // Sayaç gösterilemese de tarama devam eder.
    }
  }, [tenant.slug, tenant.features.expectedList]);

  /**
   * Hızlı okumada her kayıt için ayrı sayaç isteği atmamak için toplanır:
   * arka arkaya 10 eşleştirme yapılsa da tek istek gider.
   */
  const statsZaman = useRef<ReturnType<typeof setTimeout> | null>(null);
  const statsIste = useCallback(() => {
    if (statsZaman.current) clearTimeout(statsZaman.current);
    statsZaman.current = setTimeout(() => void statsYenile(), 800);
  }, [statsYenile]);

  useEffect(() => {
    void statsYenile();
    return () => {
      if (statsZaman.current) clearTimeout(statsZaman.current);
    };
  }, [statsYenile]);

  // --- Kuyruk kurulumu
  useEffect(() => {
    const q = new SendQueue(tenant.slug, {
      onPendingChange: setBekleyen,
      onSuccess: (item, res) => {
        satirGuncelle(item.clientUuid, {
          durum: res.replaced ? 'degistirildi' : 'kayitli',
          not: res.replaced
            ? `önceki eşleşme silindi: ${res.replaced.codeB ?? ''}`
            : undefined,
        });
        statsIste();
      },
      onConflict: (item, conflict) => {
        // Yerel önbellek kaçırmış: sunucu çakışma bildirdi.
        sesUyari();
        const eslesen =
          conflict.existing.codeA === item.codeA
            ? conflict.existing.codeB
            : conflict.existing.codeA;
        satirGuncelle(item.clientUuid, {
          durum: 'hata',
          not:
            conflict.kind === 'duplicate'
              ? 'zaten kayıtlı'
              : `çakışma: ${eslesen ?? ''}`,
        });
        // Yerel önbelleği sunucudaki gerçekle hizala.
        if (conflict.existing.codeA) {
          kullanilan.current.set(conflict.existing.codeA, conflict.existing.codeB ?? '');
        }
        if (conflict.existing.codeB) {
          kullanilan.current.set(conflict.existing.codeB, conflict.existing.codeA ?? '');
        }
      },
      onError: (item, message) => {
        sesHata();
        satirGuncelle(item.clientUuid, { durum: 'hata', not: message });
      },
    });

    kuyruk.current = q;
    return () => q.stop();
  }, [tenant.slug, satirGuncelle, statsIste]);

  // --- Referans eşleştirme listesi (LCW)
  useEffect(() => {
    if (!referansKural) return;
    let iptal = false;

    void api
      .get<{ pairs: string[][]; sonYukleme: string | null; tamamlandi: boolean }>(
        `/${tenant.slug}/reference`,
      )
      .then((res) => {
        if (iptal) return;
        const harita = new Map<string, string>();
        for (const [a, b] of res.pairs) {
          if (a && b) harita.set(a.trim().toUpperCase(), b.trim().toUpperCase());
        }
        referans.current = harita;
        setReferansSayisi(harita.size);
        setSonYukleme(res.sonYukleme);
        setYuklemeTam(res.tamamlandi);
      })
      .catch(() => {
        // Liste inmezse doğrulama sunucuda yine yapılıyor; okutma durmaz.
      })
      .finally(() => {
        if (!iptal) setReferansHazir(true);
      });

    return () => {
      iptal = true;
    };
  }, [referansKural, tenant.slug]);

  // --- Vardiya önbelleği
  useEffect(() => {
    let iptal = false;

    (async () => {
      try {
        const res = await api.get<{ date: string; pairs: string[][] }>(
          `/${tenant.slug}/cache`,
        );
        if (iptal) return;
        const bugunkuSatirlar: Satir[] = [];
        for (const [a, b, k] of res.pairs) {
          if (a) kullanilan.current.set(a, b ?? '');
          if (b) kullanilan.current.set(b, a ?? '');
          if (a && b) {
            bugunkuSatirlar.push({
              id: `cache-${a}-${b}`,
              codeA: a,
              codeB: b,
              durum: 'kayitli',
              // Önbellek yalnızca kodları taşıyor; doğrulama aynı kuralla
              // burada yeniden hesaplanıyor.
              gecerli: isMatchValid(a, b, tenant.matchRule),
              kategori: k || undefined,
            });
          }
        }
        setSatirlar((eski) => {
          const mevcut = new Set(eski.map((s) => `${s.codeA}\u0000${s.codeB}`));
          const eklenecek = bugunkuSatirlar.filter(
            (s) => !mevcut.has(`${s.codeA}\u0000${s.codeB}`),
          );
          return [...eski, ...eklenecek].slice(0, 200);
        });
      } catch {
        // Önbellek yüklenemese de çalışılabilir; sunucu 409 ile korur.
      } finally {
        if (!iptal) setHazir(true);
      }
    })();

    return () => {
      iptal = true;
    };
  }, [tenant.slug]);

  // --- Ekran kapanmasın
  useEffect(() => {
    let birak: (() => void) | undefined;
    void keepScreenAwake().then((f) => (birak = f));
    return () => birak?.();
  }, []);

  const sifirla = useCallback(() => {
    setSlotA('');
    setSlotB('');
  }, []);

  /** İki slot da dolduğunda çağrılır. */
  const esle = useCallback(
    (a: string, b: string, overwrite: boolean, kategori?: string) => {
      const ek = kategori ? { kategori } : undefined;

      const item: QueueItem = {
        clientUuid: crypto.randomUUID(),
        codeA: a,
        codeB: b,
        overwrite,
        data: ek,
        queuedAt: Date.now(),
      };

      // Yerel önbelleği hemen güncelle: sıradaki okuma doğru uyarıyı versin.
      kullanilan.current.set(a, b);
      /**
       * "Ürün kodu yok" nöbet değeri önbelleğe girmez: girseydi ikinci kez
       * kullanıldığında "bu kod daha önce eşleştirildi" uyarısı çıkardı,
       * oysa birden çok gönderinin ürün kodu olmayabilir.
       */
      if (b !== URUN_KODU_YOK) kullanilan.current.set(b, a);

      /**
       * Doğrulama anında yapılır: operatör hatalı etiketi okuttuğu saniyede
       * duymalı. Sunucu da aynı kuralı uyguluyor; buradaki sonuç yalnızca
       * geri bildirim için.
       */
      /**
       * LCW'de doğruluk kuralla değil listeyle belirleniyor. "Listede yok"
       * hata değil: elde karşılaştıracak veri olmadığı anlamına geliyor ve
       * operatörün elle kontrol etmesi gerekiyor.
       */
      const durum = referansKural
        ? referansDurumu(a, b, referans.current)
        : undefined;
      const gecerli = durum ? durum === 'dogru' : isMatchValid(a, b, tenant.matchRule);

      if (durum === 'listede-yok') setListedeYok({ a, b });
      if (durum === 'yanlis') {
        setYanlisEslesme({
          a,
          b,
          dogruB: referans.current.get(a.trim().toUpperCase()) ?? '',
        });
      }

      setSatirlar((eski) => [
        {
          id: item.clientUuid,
          codeA: a,
          codeB: b,
          durum: 'bekliyor',
          gecerli,
          kategori,
          referans: durum,
        },
        ...eski.slice(0, 199),
      ]);

      kuyruk.current?.push(item);

      if (durum === 'listede-yok') {
        // Kendi sesi var: ne başarı ne hata — dikkat isteyen bir durum.
        sesUyari();
        vibrate(140);
      } else if (gecerli) {
        sesEslestirmeTamam();
        vibrate(50);
      } else {
        // Hatalı eşleşme kaydedilir ama farklı ses ve uzun titreşimle bildirilir.
        sesHata();
        vibrate(220);
      }
      sifirla();
    },
    [sifirla, tenant.matchRule, referansKural],
  );

  /**
   * Okunan kodu işler. Kritik yol — burada ağ çağrısı YOKTUR.
   */
  const okundu = useCallback(
    (ham: string) => {
      const kod = ham.trim();
      if (!kod) return;

      unlockAudio();

      /**
       * Kategoriye ait olmayan kodlar reddedilir.
       *
       * Kamera sürekli açık çalıştığı için kadrajdan geçen raf etiketi,
       * ambalaj barkodu veya alakasız her kod aksi halde eşleştirmeye
       * girerdi. Kural tanımlı olmayan kategorilerde filtre uygulanmaz.
       */
      if (!isAcceptedCode(kod, tenant.scanRules)) {
        setReddedilen(kod);
        setSiraUyarisi(null);
        sesUyari();
        return;
      }
      setReddedilen(null);

      const taraf = detectScanSide(kod, tenant.scanRules);

      // Hangi slota gireceğini belirle: önek tanınmadıysa sıraya göre koy.
      let hedef: ScanSide;
      if (taraf) hedef = taraf;
      else if (!slotA) hedef = 'a';
      else hedef = 'b';

      if (!slotA && hedef === 'b') {
        setSiraUyarisi(
          `${kod} ${tenant.scanRules.b.label.toLowerCase()} gibi görünüyor. ` +
            `Önce ${tenant.scanRules.a.label.toLowerCase()} okutun.`,
        );
        sesUyari();
        vibrate(120);
        return;
      }

      setSiraUyarisi(null);

      // Aynı kodu iki kez okutmak bir şey değiştirmesin.
      if ((hedef === 'a' && slotA === kod) || (hedef === 'b' && slotB === kod)) {
        return;
      }

      /**
       * Bu kod daha önce eşleştirilmiş mi? Uyarı İLK okumada çıkar —
       * operatör boşuna ikinci barkodu okutmasın.
       */
      const eslesen = kullanilan.current.get(kod);
      if (eslesen !== undefined && !izinli.current.has(kod)) {
        sesUyari();
        vibrate(120);
        onayAc(kod, eslesen, hedef);
        return;
      }

      if (hedef === 'a') {
        setSlotA(kod);
        sesIlkOkuma();
        vibrate(30);
      } else {
        setSlotB(kod);
        if (slotA) {
          const ustuneYaz = izinli.current.has(kod) || izinli.current.has(slotA);
          if (kategoriSorulur) {
            sesIlkOkuma();
            vibrate(30);
            setKategoriSecimi({ a: slotA, b: kod, ustuneYaz });
          } else {
            esle(slotA, kod, ustuneYaz);
          }
        } else {
          sesIlkOkuma();
          vibrate(30);
        }
      }
    },
    [slotA, slotB, tenant.scanRules, esle, onayAc, kategoriSorulur],
  );

  /**
   * Okuma kaynakları.
   *
   * DataWedge (el terminali) ve keyboard-wedge birlikte dinlenir: DataWedge
   * yapılandırılmamış bir terminalde okuyucu klavye gibi yazar, o zaman
   * ikincisi devreye girer. İkisi aynı anda tetiklenirse aynı kod arka arkaya
   * gelir; `okundu` aynı kodu yok saydığı için sorun çıkmaz.
   */
  useEffect(() => {
    // Onay, kategori seçimi ya da hata paneli açıkken okuma alınmaz.
    if (onay || kategoriSecimi || yanlisEslesme) return;

    const wedgeDurdur = startScannerListener({ onScan: okundu, onBuffer: setTampon });
    const dwDurdur = onHardwareScan(okundu);

    return () => {
      wedgeDurdur();
      dwDurdur();
    };
  }, [okundu, onay, kategoriSecimi, yanlisEslesme]);

  function onayla() {
    if (!onay) return;
    izinli.current.add(onay.kod);
    const { kod, taraf } = onay;
    setOnay(null);
    setOnayHata(null);

    if (taraf === 'a') {
      setSlotA(kod);
      sesIlkOkuma();
    } else {
      setSlotB(kod);
      if (slotA) {
        if (kategoriSorulur) setKategoriSecimi({ a: slotA, b: kod, ustuneYaz: true });
        else esle(slotA, kod, true);
      } else sesIlkOkuma();
    }
  }

  /** İade akışı: ürün kodu bulunmayan gönderi. Kategori yine sorulur. */
  function urunKoduYok() {
    if (!slotA) return;
    setSlotB(URUN_KODU_YOK);
    const ustuneYaz = izinli.current.has(slotA);
    if (kategoriSorulur) {
      setKategoriSecimi({ a: slotA, b: URUN_KODU_YOK, ustuneYaz });
    } else {
      esle(slotA, URUN_KODU_YOK, ustuneYaz);
    }
  }

  function kategoriSec(kategori: string) {
    if (!kategoriSecimi) return;
    const { a, b, ustuneYaz } = kategoriSecimi;
    setKategoriSecimi(null);
    // Liste seçilen kategoriye geçer: eklenen gönderi gözden kaybolmasın.
    kategoriDegistir(kategori);
    esle(a, b, ustuneYaz, kategori);
  }


  function reddet() {
    setOnay(null);
    setOnayHata(null);
    sifirla();
  }

  async function oncekiEslesmeyiSil() {
    if (!onay?.recordId) return;

    setOnaySiliniyor(true);
    setOnayHata(null);
    try {
      await api.del(`/${tenant.slug}/records/${onay.recordId}`);
      kullanilan.current.delete(onay.kod);
      if (onay.eslesenKod) kullanilan.current.delete(onay.eslesenKod);
      izinli.current.delete(onay.kod);
      setSatirlar((eski) =>
        eski.filter(
          (s) =>
            !(
              (s.codeA === onay.kod && s.codeB === onay.eslesenKod) ||
              (s.codeA === onay.eslesenKod && s.codeB === onay.kod)
            ),
        ),
      );
      setOnay(null);
      sifirla();
      statsIste();
    } catch {
      setOnayHata('Önceki eşleştirme silinemedi');
    } finally {
      setOnaySiliniyor(false);
    }
  }

  const kuralA = tenant.scanRules.a.label;
  const kuralB = tenant.scanRules.b.label;

  /**
   * Sekme açıkken yalnızca o kategorinin gönderileri listelenir; kategorisi
   * bilinmeyen eski kayıtlar (vardiya önbelleğinden gelen, iade akışı
   * eklenmeden önce oluşmuş satırlar) her sekmede görünür ki kaybolmasınlar.
   */
  const gorunenSatirlar = useMemo(
    () =>
      kategoriler.length === 0
        ? satirlar
        : satirlar.filter((s) => !s.kategori || s.kategori === aktifKategori),
    [satirlar, kategoriler.length, aktifKategori],
  );

  /**
   * Liste bugünden eskiyse vurgulanır.
   *
   * Sessiz bir yükleme arızası (anahtar uyuşmazlığı, gönderen servisin
   * durması) en tehlikeli durum: doğrulama çalışmaya devam eder ama eski
   * listeyle. Tarih ekranda durursa operatör fark eder.
   */
  const { yuklemeMetni, listeEski } = useMemo(() => {
    if (!sonYukleme) return { yuklemeMetni: 'yükleme tarihi yok', listeEski: true };

    const tarih = new Date(sonYukleme);
    if (Number.isNaN(tarih.getTime())) {
      return { yuklemeMetni: 'yükleme tarihi okunamadı', listeEski: true };
    }

    const gunFarki = Math.floor((Date.now() - tarih.getTime()) / 86_400_000);
    const metin = tarih.toLocaleString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });

    return {
      yuklemeMetni: gunFarki >= 1 ? `${metin} yüklendi (${gunFarki} gün önce)` : `${metin} yüklendi`,
      listeEski: gunFarki >= 1,
    };
  }, [sonYukleme]);

  /** LCW sayaçları: doğru, yanlış ve elle kontrol bekleyen. */
  const referansSayaclari = useMemo(() => {
    let dogru = 0;
    let yanlis = 0;
    let yok = 0;
    for (const s of satirlar) {
      if (s.referans === 'dogru') dogru++;
      else if (s.referans === 'yanlis') yanlis++;
      else if (s.referans === 'listede-yok') yok++;
    }
    return { dogru, yanlis, yok };
  }, [satirlar]);

  const sayac = useMemo(
    () => satirlar.filter((s) => s.durum !== 'hata').length,
    [satirlar],
  );

  return (
    <div className="tarama">
      <header className="ust-bar">
        <button className="btn-geri" onClick={onBack} aria-label="Geri">
          ‹
        </button>
        <h1>{tenant.name}</h1>
        <div className="ust-bar-sag">
          {bekleyen > 0 && <span className="rozet-bekleyen">{bekleyen} gönderiliyor</span>}
          <span>{sayac} eşleştirme</span>
        </div>
      </header>

      {tenant.features.expectedList && stats && stats.total > 0 && (
        <button
          className="sayac-cubugu"
          onClick={() => setPanelAcik(true)}
          title="Okutulmayan barkodları göster"
        >
          <span className="sayac-parca">
            <strong>{stats.scanned}</strong> okutuldu
          </span>
          <span className="sayac-parca vurgu">
            <strong>{stats.remaining}</strong> kaldı
          </span>
          <span className="sayac-parca soluk">{stats.total} toplam</span>
          <span className="sayac-ok">›</span>
        </button>
      )}

      {referansKural && (
        <div className="referans-cubuk">
          <span className="referans-parca dogru">
            <strong>{referansSayaclari.dogru}</strong> doğru
          </span>
          <span className="referans-parca yanlis">
            <strong>{referansSayaclari.yanlis}</strong> yanlış
          </span>
          <span className="referans-parca yok">
            <strong>{referansSayaclari.yok}</strong> listede yok
          </span>
          <span
            className={`referans-parca ${
              !yuklemeTam ? 'yarim' : listeEski ? 'eski' : 'soluk'
            }`}
          >
            {!referansHazir
              ? 'liste yükleniyor…'
              : referansSayisi === 0
                ? 'referans listesi boş'
                : !yuklemeTam
                  ? `${referansSayisi} eşleştirme · YÜKLEME YARIM KALDI`
                  : `${referansSayisi} eşleştirme · ${yuklemeMetni}`}
          </span>
        </div>
      )}

      {kameraAcik && (
        <CameraScanner onScan={okundu} onClose={() => kameraDegistir(false)} />
      )}

      <div className="slotlar">
        <div className={`slot ${slotA ? 'dolu' : ''}`}>
          <span className="slot-etiket">{kuralA}</span>
          <span className="slot-deger">{slotA || '—'}</span>
        </div>
        <div className="slot-ok">↔</div>
        <div className={`slot ${slotB ? 'dolu' : ''}`}>
          <span className="slot-etiket">{kuralB}</span>
          <span className="slot-deger">{slotB || '—'}</span>
        </div>
      </div>

      {satirlar.length === 0 && getNativeSummary() && (
        <div className="cihaz-bilgi">{getNativeSummary()}</div>
      )}

      {tampon && (
        <div className="tampon-gosterge">
          okunuyor: <strong>{tampon}</strong>
        </div>
      )}

      {listedeYok && (
        <div className="listede-yok-panel">
          <div>
            <strong>Listede yok — manuel kontrol edin</strong>
            <p>
              <span className="kod">{listedeYok.a}</span> yüklenen referans
              listesinde bulunmuyor, bu yüzden eşleştirme doğrulanamadı. Kayıt
              alındı; sipariş kodunu elle karşılaştırın.
            </p>
            <p className="listede-yok-kodlar">
              Okutulan sipariş kodu: <span className="kod">{listedeYok.b}</span>
            </p>
          </div>
          <button onClick={() => setListedeYok(null)} aria-label="Kapat">
            ✕
          </button>
        </div>
      )}

      {reddedilen && (
        <div className="reddedilen-kod">
          <span>
            <strong>{reddedilen}</strong> bu kategoriye ait değil
          </span>
          <button onClick={() => setReddedilen(null)} aria-label="Kapat">
            ✕
          </button>
        </div>
      )}

      {siraUyarisi && (
        <div className="reddedilen-kod">
          <span>{siraUyarisi}</span>
          <button onClick={() => setSiraUyarisi(null)} aria-label="Kapat">
            ✕
          </button>
        </div>
      )}

      <div className="tarama-durum">
        {!hazir
          ? 'Vardiya listesi yükleniyor…'
          : !slotA && !slotB
            ? 'Barkod okutun'
            : 'İkinci barkodu okutun'}
        {/* İade akışı: bazı gönderilerin ürün kodu yok. Düğme yalnızca
            barkod okunmuşken görünür — sırayı bozan bir kayıt oluşmasın. */}
        {tenant.features.returnFlow && slotA && !slotB && (
          <button className="btn-kod-yok" onClick={urunKoduYok}>
            Ürün kodu yok
          </button>
        )}
        {(slotA || slotB) && (
          <button className="btn-sifirla" onClick={sifirla}>
            Sıfırla
          </button>
        )}
      </div>

      <div className="manuel-giris">
        <input
          data-manual-input="true"
          value={manuel}
          onChange={(e) => setManuel(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && manuel.trim()) {
              okundu(manuel);
              setManuel('');
            }
          }}
          placeholder="Elle kod girin ve Enter"
        />
        {cameraSupported() && !hasHardwareScanner() && (
          <button
            className={`btn-kamera ${kameraAcik ? 'acik' : ''}`}
            onClick={() => kameraDegistir(!kameraAcik)}
            title={kameraAcik ? 'Kamerayı kapat' : 'Kamerayı aç'}
          >
            {kameraAcik ? 'Kamera açık' : 'Kamera'}
          </button>
        )}
      </div>

      {kategoriler.length > 0 && (
        <div className="kategori-sekmeler">
          {kategoriler.map((k) => (
            <button
              key={k}
              className={k === aktifKategori ? 'aktif' : ''}
              onClick={() => kategoriDegistir(k)}
            >
              {k}
              <span className="sekme-sayi">
                {satirlar.filter((s) => s.kategori === k).length}
              </span>
            </button>
          ))}
        </div>
      )}
      <ul className="satir-liste">
        {gorunenSatirlar.map((s) => (
          <li key={s.id} className={`satir ${s.durum} ${s.gecerli ? '' : 'gecersiz'}`}>
            <span className="satir-kodlar">
              {s.codeA} <span className="ok">↔</span> {s.codeB}
            </span>
            <span className="satir-durum">
              {s.referans === 'listede-yok' ? (
                <span className="rozet-yok">listede yok</span>
              ) : (
                !s.gecerli && <span className="rozet-gecersiz">eşleşmiyor</span>
              )}
              {s.durum === 'bekliyor' && '⋯'}
              {s.durum === 'kayitli' && '✓'}
              {s.durum === 'degistirildi' && '✓ değiştirildi'}
              {s.durum === 'hata' && `✕ ${s.not ?? ''}`}
            </span>
          </li>
        ))}
      </ul>

      {panelAcik && (
        <RemainingPanel
          tenantSlug={tenant.slug}
          onClose={() => {
            setPanelAcik(false);
            void statsYenile();
          }}
        />
      )}

      {kategoriSecimi && (
        <div className="onay-katman" role="dialog" aria-modal="true">
          <div className="onay-kutu">
            <h2>Kategori seçin</h2>
            <p className="onay-kod">{kategoriSecimi.b}</p>
            <p className="onay-aciklama">
              Barkod: <strong>{kategoriSecimi.a}</strong>
            </p>
            <div className="kategori-secenekler">
              {tenant.subCategories.map((k, i) => (
                <button key={k} onClick={() => kategoriSec(k)} autoFocus={i === 0}>
                  {k}
                </button>
              ))}
            </div>
            <div className="onay-butonlar">
              <button
                className="btn-iptal"
                onClick={() => {
                  setKategoriSecimi(null);
                  sifirla();
                }}
              >
                İptal
              </button>
            </div>
          </div>
        </div>
      )}

      {yanlisEslesme && (
        <div className="onay-katman" role="dialog" aria-modal="true">
          <div className="onay-kutu hatali">
            <h2 className="hata-baslik">Hatalı eşleştirme</h2>
            <p className="onay-kod">{yanlisEslesme.a}</p>

            <div className="karsilastirma-satir">
              <span className="karsilastirma-etiket">Okutulan</span>
              <span className="kod kirmizi-metin">{yanlisEslesme.b}</span>
            </div>
            <div className="karsilastirma-satir">
              <span className="karsilastirma-etiket">Doğrusu</span>
              <span className="kod yesil-metin">{yanlisEslesme.dogruB || '—'}</span>
            </div>

            <p className="onay-uyari">
              Kayıt alındı ve hatalı işaretlendi. Gösterge panelinden
              görebilirsiniz.
            </p>

            <div className="onay-butonlar">
              <button
                className="btn-devam"
                onClick={() => setYanlisEslesme(null)}
                autoFocus
              >
                Tamam
              </button>
            </div>
          </div>
        </div>
      )}

      {onay && (
        <div className="onay-katman" role="dialog" aria-modal="true">
          <div className="onay-kutu">
            <h2>Bu barkod zaten eşleştirilmiş</h2>
            <p className="onay-kod">{onay.kod}</p>
            <p className="onay-aciklama">
              Şununla eşleşmiş: <strong>{onay.eslesenKod || '(bilinmiyor)'}</strong>
            </p>
            <p className="onay-uyari">
              Devam ederseniz eski eşleştirme silinecek ve yenisi kaydedilecek.
            </p>
            {onayHata && <div className="hata-kutu">{onayHata}</div>}
            <div className="onay-butonlar">
              <button className="btn-iptal" onClick={reddet}>
                İptal
              </button>
              <button
                className="btn-sil"
                onClick={() => void oncekiEslesmeyiSil()}
                disabled={!onay.recordId || onaySiliniyor}
              >
                {onaySiliniyor ? 'Siliniyor…' : 'Önceki eşleştirmeyi sil'}
              </button>
              <button className="btn-devam" onClick={onayla} autoFocus>
                Devam et
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
